import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { heroData } from '../../data/mock';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const Hero = () => {
  return (
    <section className="relative min-h-[auto] lg:min-h-[90vh] flex items-center overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100" />
      
      {/* Decorative Elements - hidden on mobile to prevent overflow */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-blue-200/30 rounded-full blur-3xl hidden sm:block" />
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-blue-100/40 rounded-full blur-3xl hidden sm:block" />
      
      <div className="container mx-auto px-4 py-6 lg:py-24 relative z-10 w-full max-w-full box-border">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-16 items-center">
          {/* Content */}
          <div className="w-full max-w-xl lg:max-w-xl">
            {/* Mobile-optimized H1 */}
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-1.5 lg:mb-4">
              {/* Mobile: Short version */}
              <span className="lg:hidden">
                Beste Finanzierung
                <span className="block text-blue-600">– schnell & persönlich</span>
              </span>
              {/* Desktop: Original version */}
              <span className="hidden lg:block">
                {heroData.title}
                <span className="block text-blue-600 mt-2">– {heroData.subtitle}</span>
              </span>
            </h1>
            
            {/* Mobile-optimized subtitle */}
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 mb-3 lg:mb-8 leading-relaxed">
              <span className="lg:hidden">Vergleich mit über 500 Partnerbanken für Ihre optimale Finanzierung.</span>
              <span className="hidden lg:block">{heroData.description}</span>
            </p>

            {/* Mobile: Single CTA Button - min tap target 44px */}
            <div className="lg:hidden">
              <Link to="/anfrage" className="block">
                <Button size="lg" className="w-full min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 group px-6 text-sm sm:text-base">
                  Jetzt unverbindlich anfragen
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            {/* Desktop: Both CTA Buttons */}
            <div className="hidden lg:flex flex-row gap-4 mb-8">
              <Link to="/anfrage">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 group px-8">
                  {heroData.ctaText}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/beratung">
                <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50">
                  Beratung buchen
                </Button>
              </Link>
            </div>

            {/* Mobile: Trust Signals in one line - compact layout */}
            <div className="lg:hidden mt-3 flex items-center gap-3">
              <Link 
                to="/partner" 
                className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-2.5 py-1.5 rounded-full text-xs font-medium hover:bg-blue-200 transition-colors flex-shrink-0"
                aria-label="Unsere Partner ansehen"
              >
                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" />
                500+ Partner
              </Link>
              <a 
                href="#kundenstimmen" 
                className="flex items-center gap-1 flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('kundenstimmen')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <div className="flex items-center">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-3 h-3 text-amber-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <span className="text-xs text-slate-600 font-medium ml-1">4,9/5</span>
              </a>
            </div>

            {/* Mobile: Benefits Pills - Wrapped layout for full visibility */}
            <div className="lg:hidden mt-3 w-full">
              <div className="flex flex-wrap gap-2">
                {['Kostenlose Beratung', 'Beste Konditionen', 'Schnelle Zusage'].map((benefit) => (
                  <div key={benefit} className="flex items-center gap-1 bg-white px-2.5 py-1.5 rounded-full shadow-sm border border-slate-100">
                    <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                    <span className="text-xs font-medium text-slate-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile: Hero Image below text - contained within bounds */}
            <div className="lg:hidden mt-4 relative rounded-xl overflow-hidden shadow-lg w-full box-border">
              <img
                src={heroData.backgroundImage}
                alt="Finanzberatung"
                loading="eager"
                className="w-full h-44 object-cover rounded-xl"
              />
              <div className="absolute bottom-2 right-2 bg-white/95 backdrop-blur-sm rounded-lg px-2 py-1 shadow-md">
                <p className="text-sm font-bold text-blue-600">2,8%</p>
                <p className="text-[9px] text-slate-500">Effektivzins ab</p>
              </div>
            </div>

            {/* Mobile: Secondary CTA below hero image - min tap target 44px */}
            <div className="lg:hidden mt-3 pb-4">
              <Link to="/beratung" className="block">
                <Button size="lg" variant="outline" className="w-full min-h-[44px] border-slate-300 text-slate-700 hover:bg-slate-50">
                  Beratung buchen
                </Button>
              </Link>
            </div>

            {/* Desktop: Partner Badge */}
            <div className="hidden lg:block mb-6">
              <Link 
                to="/partner" 
                className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-200 transition-colors cursor-pointer"
                aria-label="Unsere Partner ansehen"
              >
                <span className="w-2 h-2 bg-blue-600 rounded-full animate-pulse" />
                Über 500 Partnerbanken im Vergleich
              </Link>
            </div>

            {/* Desktop: Quick Benefits */}
            <div className="hidden lg:flex flex-wrap gap-4 mb-8">
              {['Kostenlose Beratung', 'Beste Konditionen', 'Schnelle Zusage'].map((benefit) => (
                <div key={benefit} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* Desktop: Trust Indicators */}
            <div className="hidden lg:block mt-10 pt-8 border-t border-slate-200">
              <div className="flex items-center gap-6">
                <div className="flex -space-x-3">
                  {['MS', 'TM', 'SW', 'AF'].map((initials, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-white shadow-md"
                    >
                      {initials}
                    </div>
                  ))}
                </div>
                <a 
                  href="#kundenstimmen"
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('kundenstimmen')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 mt-1">4.9/5 von über 2.500 Kunden</p>
                </a>
              </div>
            </div>
          </div>

          {/* Desktop: Image Section */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={heroData.backgroundImage}
                  alt="Finanzberatung"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
              </div>

              {/* Floating Card 1 */}
              <div className="absolute -left-8 top-20 bg-white rounded-xl shadow-xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">Zusage erhalten</p>
                    <p className="text-xs text-slate-500">vor 2 Minuten</p>
                  </div>
                </div>
              </div>

              {/* Floating Card 2 */}
              <div className="absolute -right-4 bottom-24 bg-white rounded-xl shadow-xl p-4 animate-float-delayed">
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">2,8%</p>
                  <p className="text-xs text-slate-500">Effektivzins ab</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 4s ease-in-out infinite 0.5s;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default Hero;
